import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Loader2, Users, Inbox, MapPin, Database, Tag, FileText, Hash, GitBranch } from 'lucide-react';
import { newslettersApi, submissionsApi, NewsletterRecord, SubmissionRecord, extractLookupDisplay } from '../services/creatorApi';
import { toast } from 'sonner';
export function NewsletterDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [newsletter, setNewsletter] = useState<NewsletterRecord | null>(null);
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'subscriptions'>('submissions');
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const newsletterData = await newslettersApi.get(id);
        setNewsletter(newsletterData);
        // Load submissions for this newsletter
        if (newsletterData?.Newsletter_ID) {
          setLoadingSubmissions(true);
          try {
            const subs = await submissionsApi.findByNewsletterId(newsletterData.Newsletter_ID);
            setSubmissions(subs);
          } catch {
            // Non-blocking
          } finally {
            setLoadingSubmissions(false);
          }
        }
      } catch (err) {
        console.error('Failed to load newsletter:', err);
        toast.error('Failed to load newsletter details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-3428">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3429" />
      </div>;
  }
  if (!newsletter) {
    return <div className="text-center py-12" data-id="element-3430">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-3431">
          Newsletter not found
        </h3>
        <Link to="/newsletters" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500" data-id="element-3432">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-3433" />
          Back to Newsletters
        </Link>
      </div>;
  }
  // Get tag names from subform
  const tagNames = (newsletter.Newsletter_Tags || []).map(t => t.Tag_Name);
  // Get rules from subform
  const rules = newsletter.Newsletter_Category_Rules || [];
  // Split submissions by status
  const confirmedSubmissions = submissions.filter(s => s.Status === 'Confirmed');
  const pendingSubmissions = submissions.filter(s => s.Status !== 'Confirmed');
  return <div className="space-y-6" data-id="element-3434">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3435">
        <div className="flex items-center gap-4" data-id="element-3436">
          <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3437">
            <ArrowLeft className="h-5 w-5" data-id="element-3438" />
          </Link>
          <div data-id="element-3439">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3440">
              {newsletter.Newsletter}
            </h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500" data-id="element-3441">
              <span className="flex items-center gap-1" data-id="element-3442">
                <MapPin className="h-3.5 w-3.5" data-id="element-3443" />
                {newsletter.Region || 'All Regions'}
              </span>
              <span className="flex items-center gap-1" data-id="element-3444">
                <Users className="h-3.5 w-3.5" data-id="element-3445" />
                {newsletter.Team}
              </span>
              {newsletter.Newsletter_ID && <span className="flex items-center gap-1 font-mono text-xs text-gray-400" data-id="element-3446">
                  <Hash className="h-3 w-3" data-id="element-3447" />
                  {newsletter.Newsletter_ID}
                </span>}
            </div>
          </div>
        </div>
        <Link to={`/newsletters/${id}/edit`} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3448">
          <Edit2 className="h-4 w-4" data-id="element-3449" />
          Edit Configuration
        </Link>
      </div>

      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3450">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3451">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-3452">Source</h3>
          <div className="flex items-start gap-3" data-id="element-3453">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-3454">
              <FileText className="h-5 w-5 text-blue-600" data-id="element-3455" />
            </div>
            <div data-id="element-3456">
              <div className="text-sm font-medium text-gray-900" data-id="element-3457">
                {newsletter.Sign_Up_Form ? extractLookupDisplay(newsletter.Sign_Up_Form) : 'No form linked'}
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-3458">Sign Up Form</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3459">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-3460">
            CRM Destination
          </h3>
          <div className="space-y-4" data-id="element-3461">
            <div className="flex items-start gap-3" data-id="element-3462">
              <div className="p-2 bg-purple-50 rounded-lg" data-id="element-3463">
                <Database className="h-5 w-5 text-purple-600" data-id="element-3464" />
              </div>
              <div data-id="element-3465">
                {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 0 ? <div className="space-y-1" data-id="element-3466">
                    {newsletter.Mailing_Lists.map((ml, i) => <div key={i} data-id="element-3467">
                        <div className="text-sm font-medium text-gray-900" data-id="element-3468">
                          {ml.Mailing_List_Name}
                        </div>
                        {ml.Mailing_List_ID && <div className="text-xs text-gray-400 font-mono" data-id="element-3469">
                            ID: {ml.Mailing_List_ID}
                          </div>}
                      </div>)}
                  </div> : <div className="text-sm font-medium text-gray-900" data-id="element-3470">
                    Not assigned
                  </div>}
                <div className="text-xs text-gray-500 mt-1" data-id="element-3471">
                  {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 1 ? 'Mailing Lists' : 'Mailing List'}
                </div>
              </div>
            </div>
            {tagNames.length > 0 && <div className="flex items-start gap-3" data-id="element-3472">
                <div className="p-2 bg-green-50 rounded-lg" data-id="element-3473">
                  <Tag className="h-5 w-5 text-green-600" data-id="element-3474" />
                </div>
                <div data-id="element-3475">
                  <div className="flex flex-wrap gap-1" data-id="element-3476">
                    {tagNames.map((name, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3477">
                        {name}
                      </span>)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-3478">Applied Tags</div>
                </div>
              </div>}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3479">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-3480">Automation</h3>
          <div className="flex items-center justify-between" data-id="element-3481">
            <div data-id="element-3482">
              <div className="text-2xl font-semibold text-gray-900" data-id="element-3483">
                {rules.length}
              </div>
              <div className="text-sm text-gray-500" data-id="element-3484">Category Rules</div>
            </div>
            <div className="h-12 w-12 bg-orange-50 rounded-full flex items-center justify-center" data-id="element-3485">
              <GitBranch className="h-6 w-6 text-orange-600" data-id="element-3486" />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600" data-id="element-3487">
            <span className="font-medium" data-id="element-3488">{submissions.length}</span> total
            submissions
            {confirmedSubmissions.length > 0 && <span className="text-green-600 ml-1" data-id="element-3489">
                ({confirmedSubmissions.length} confirmed)
              </span>}
          </div>
        </div>
      </div>

      {/* Category Rules Summary */}
      {rules.length > 0 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-3490">
          <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3491">
            Category Rules
          </h3>
          <div className="space-y-2" data-id="element-3492">
            {rules.map((rule, i) => <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-md" data-id="element-3493">
                <span className="text-gray-500" data-id="element-3494">If</span>
                <span className="font-medium text-gray-900" data-id="element-3495">
                  {rule.Form_Field}
                </span>
                <span className="text-gray-500" data-id="element-3496">
                  {rule.Condition.replace(/_/g, ' ')}
                </span>
                {rule.Value && <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded" data-id="element-3497">
                    {rule.Value}
                  </span>}
                <span className="text-gray-500" data-id="element-3498">→</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100" data-id="element-3499">
                  {rule.Category_Name}
                </span>
              </div>)}
          </div>
        </div>}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-3500">
        <div className="border-b border-gray-200" data-id="element-3501">
          <nav className="flex -mb-px" data-id="element-3502">
            <button onClick={() => setActiveTab('submissions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3503">
              All Submissions ({submissions.length})
            </button>
            <button onClick={() => setActiveTab('subscriptions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'subscriptions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3504">
              Confirmed ({confirmedSubmissions.length})
            </button>
          </nav>
        </div>

        <div className="p-6" data-id="element-3505">
          {loadingSubmissions ? <div className="text-center py-8" data-id="element-3506">
              <Loader2 className="mx-auto h-6 w-6 text-blue-500 animate-spin" data-id="element-3507" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-3508">
                Loading submissions...
              </p>
            </div> : activeTab === 'submissions' ? submissions.length === 0 ? <div className="text-center py-12" data-id="element-3509">
                <Inbox className="mx-auto h-12 w-12 text-gray-300" data-id="element-3510" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-3511">
                  No submissions yet
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-3512">
                  Form submissions will appear here once the form is live and
                  customers start signing up.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-3513">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3514">
                  <thead className="bg-gray-50" data-id="element-3515">
                    <tr data-id="element-3516">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3517">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3518">
                        Payload Preview
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3519">
                        ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3520">
                    {submissions.map(sub => {
                let payloadPreview = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  payloadPreview = p.email || p.Email || JSON.stringify(p).substring(0, 60) + '...';
                } catch {
                  payloadPreview = sub.Form_Payload?.substring(0, 60) || '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-3521">
                          <td className="px-4 py-3" data-id="element-3522">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-3523">
                              {sub.Status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs" data-id="element-3524">
                            {payloadPreview}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-3525">
                            {sub.ID}
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div> : confirmedSubmissions.length === 0 ? <div className="text-center py-12" data-id="element-3526">
              <Users className="mx-auto h-12 w-12 text-gray-300" data-id="element-3527" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-3528">
                No confirmed subscriptions
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-3529">
                Subscribers who confirm their double opt-in will appear here.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-3530">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3531">
                <thead className="bg-gray-50" data-id="element-3532">
                  <tr data-id="element-3533">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3534">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3535">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-3536">
                      ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3537">
                  {confirmedSubmissions.map(sub => {
                let email = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  email = p.email || p.Email || '-';
                } catch {
                  email = '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-3538">
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-3539">
                          {email}
                        </td>
                        <td className="px-4 py-3" data-id="element-3540">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3541">
                            Confirmed
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-3542">
                          {sub.ID}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}